import { useEffect, useState } from "react";
import contentFallback from "../../data/content.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import IconPicker from "../components/IconPicker.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";

const DEFAULT_VALORES = contentFallback.aboutPage?.Philosophy?.valores || [];
const DEFAULT_LEADERSHIP = contentFallback.aboutPage?.leadership || {};
const DEFAULT_COMPANY = contentFallback.aboutPage?.company || {};

export default function CmsAboutEditor() {
  const { data, loading, source } = useCmsDoc("site", "about", contentFallback.aboutPage);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [valoresHydrated, setValoresHydrated] = useState(false);

  useEffect(() => {
    if (!data) return;
    const next = structuredClone(data);
    if (!next.Philosophy) next.Philosophy = {};
    const missingValores =
      !Array.isArray(next.Philosophy.valores) || next.Philosophy.valores.length === 0;
    if (missingValores) {
      next.Philosophy.valores = structuredClone(DEFAULT_VALORES);
    }
    if (!next.Philosophy.valoresTitle) next.Philosophy.valoresTitle = "Valores";
    if (!next.Philosophy.valoresSubtitle) {
      next.Philosophy.valoresSubtitle = "Cultura de Ingeniería Humana";
    }
    if (!next.leadership) next.leadership = {};
    if (!next.leadership.linkLabel) next.leadership.linkLabel = DEFAULT_LEADERSHIP.linkLabel || "Nuestras investigaciones";
    if (!next.leadership.linkUrl) next.leadership.linkUrl = DEFAULT_LEADERSHIP.linkUrl || "https://ckducureconsortium.org/";
    if (!next.company) next.company = {};
    if (!next.company.agdysa_cta_label) {
      next.company.agdysa_cta_label = DEFAULT_COMPANY.agdysa_cta_label || "Estrategia Preventiva";
    }
    if (!next.company.agdysa_cta_url) {
      next.company.agdysa_cta_url = DEFAULT_COMPANY.agdysa_cta_url || "/servicios?id=higiene-industrial";
    }
    if (!next.company.labsyso_cta_label) {
      next.company.labsyso_cta_label = DEFAULT_COMPANY.labsyso_cta_label || "ADN Científico";
    }
    if (next.company.labsyso_cta_url == null) {
      next.company.labsyso_cta_url = DEFAULT_COMPANY.labsyso_cta_url || "/labsyso";
    }
    setValoresHydrated(missingValores && source === "firestore");
    setForm(next);
  }, [data, source]);

  if (loading || !form) return <p className="text-sm text-brand-dark/50">Cargando Nosotros…</p>;

  const patch = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i += 1) {
        if (!cur[keys[i]]) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const valores = form.Philosophy?.valores || [];

  const updateValor = (idx, patchObj) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      if (!next.Philosophy) next.Philosophy = {};
      const list = [...(next.Philosophy.valores || [])];
      list[idx] = { ...list[idx], ...patchObj };
      next.Philosophy.valores = list;
      return next;
    });
  };

  const addValor = () => {
    setForm((prev) => {
      const next = structuredClone(prev);
      if (!next.Philosophy) next.Philosophy = {};
      next.Philosophy.valores = [
        ...(next.Philosophy.valores || []),
        { icon: "lucide:Gem", label: "Nuevo valor", text: "" },
      ];
      return next;
    });
  };

  const removeValor = (idx) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.Philosophy.valores = (next.Philosophy.valores || []).filter((_, i) => i !== idx);
      return next;
    });
  };

  const moveValor = (idx, dir) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.Philosophy.valores = moveListItem(next.Philosophy?.valores || [], idx, dir);
      return next;
    });
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { id, updatedAt, updatedBy, ...payload } = form;
      await saveCmsDoc("site", "about", payload);
      setValoresHydrated(false);
      setMessage("Nosotros guardado.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">Nosotros</h2>
        <p className="text-sm text-brand-dark/55 mt-1">Fuente: {source}</p>
      </div>

      {valoresHydrated && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Firestore no tenía la sección <strong>Valores</strong>. Se cargaron los 6 valores del JSON
          local. Pulse <strong>Guardar cambios</strong> para persistirlos en el CMS.
        </div>
      )}

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <Field label="Título">
          <input className={inputClass} value={form.title || ""} onChange={(e) => patch("title", e.target.value)} />
        </Field>
        <Field label="Subtítulo">
          <input className={inputClass} value={form.subtitle || ""} onChange={(e) => patch("subtitle", e.target.value)} />
        </Field>
        <Field label="Descripción">
          <textarea className={textareaClass} value={form.description || ""} onChange={(e) => patch("description", e.target.value)} />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Acreditación</h3>
        <Field label="Título">
          <input className={inputClass} value={form.accreditation?.title || ""} onChange={(e) => patch("accreditation.title", e.target.value)} />
        </Field>
        <Field label="Texto">
          <textarea className={textareaClass} value={form.accreditation?.text || ""} onChange={(e) => patch("accreditation.text", e.target.value)} />
        </Field>
        <Field label="Highlight">
          <textarea className={textareaClass} value={form.accreditation?.highlight || ""} onChange={(e) => patch("accreditation.highlight", e.target.value)} />
        </Field>
        <Field label="Extra">
          <textarea className={textareaClass} value={form.accreditation?.extra || ""} onChange={(e) => patch("accreditation.extra", e.target.value)} />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Filosofía</h3>
        <Field label="Misión">
          <textarea className={textareaClass} value={form.Philosophy?.mision || ""} onChange={(e) => patch("Philosophy.mision", e.target.value)} />
        </Field>
        <Field label="Visión">
          <textarea className={textareaClass} value={form.Philosophy?.vision || ""} onChange={(e) => patch("Philosophy.vision", e.target.value)} />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-bold text-lg">Valores</h3>
          <button
            type="button"
            onClick={addValor}
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
          >
            + Valor
          </button>
        </div>
        <CmsWithMediaSpecs variant="icons">
          <Field label="Título sección">
            <input
              className={inputClass}
              value={form.Philosophy?.valoresTitle || ""}
              onChange={(e) => patch("Philosophy.valoresTitle", e.target.value)}
            />
          </Field>
          <Field label="Subtítulo sección">
            <input
              className={inputClass}
              value={form.Philosophy?.valoresSubtitle || ""}
              onChange={(e) => patch("Philosophy.valoresSubtitle", e.target.value)}
            />
          </Field>

          {valores.map((val, idx) => (
            <div key={idx} className="border border-brand-soft rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/40">
                    Valor {idx + 1}
                  </p>
                  <MoveButtons
                    idx={idx}
                    total={valores.length}
                    onMove={(dir) => moveValor(idx, dir)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeValor(idx)}
                  className="text-xs text-red-600 font-bold"
                >
                  Quitar
                </button>
              </div>
              <IconPicker
                label="Icono"
                value={val.icon || "lucide:Gem"}
                onChange={(icon) => updateValor(idx, { icon })}
              />
              <Field label="Nombre">
                <input
                  className={inputClass}
                  value={val.label || ""}
                  onChange={(e) => updateValor(idx, { label: e.target.value })}
                />
              </Field>
              <Field label="Descripción">
                <input
                  className={inputClass}
                  value={val.text || ""}
                  onChange={(e) => updateValor(idx, { text: e.target.value })}
                />
              </Field>
            </div>
          ))}
        </CmsWithMediaSpecs>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Liderazgo</h3>
        <Field label="Nombre">
          <input className={inputClass} value={form.leadership?.name || ""} onChange={(e) => patch("leadership.name", e.target.value)} />
        </Field>
        <Field label="Rol">
          <input className={inputClass} value={form.leadership?.role || ""} onChange={(e) => patch("leadership.role", e.target.value)} />
        </Field>
        <Field label="Slogan">
          <input className={inputClass} value={form.leadership?.slogan || ""} onChange={(e) => patch("leadership.slogan", e.target.value)} />
        </Field>
        <CmsWithMediaSpecs variant="images">
          <Field label="Imagen URL">
            <input className={inputClass} value={form.leadership?.image || ""} onChange={(e) => patch("leadership.image", e.target.value)} />
          </Field>
        </CmsWithMediaSpecs>
        <Field label="Bio">
          <textarea className={textareaClass} value={form.leadership?.bio || ""} onChange={(e) => patch("leadership.bio", e.target.value)} />
        </Field>
        <Field label="Extra">
          <textarea className={textareaClass} value={form.leadership?.extra || ""} onChange={(e) => patch("leadership.extra", e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Texto del enlace (caja verde)">
            <input
              className={inputClass}
              value={form.leadership?.linkLabel || ""}
              placeholder="Nuestras investigaciones"
              onChange={(e) => patch("leadership.linkLabel", e.target.value)}
            />
          </Field>
          <Field label="URL del enlace">
            <input
              className={inputClass}
              value={form.leadership?.linkUrl || ""}
              placeholder="https://… o /ruta"
              onChange={(e) => patch("leadership.linkUrl", e.target.value)}
            />
          </Field>
        </div>
        <p className="text-xs text-brand-dark/50">
          Pulse <strong>Guardar cambios</strong> para publicar el enlace en Nosotros. Si solo ve los
          campos aquí pero no en el sitio, el documento Firestore aún no tiene esos valores.
        </p>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Empresa / identidad</h3>
        <Field label="Texto AGDYSA">
          <textarea className={textareaClass} value={form.company?.agdysa || ""} onChange={(e) => patch("company.agdysa", e.target.value)} />
        </Field>
        <CmsWithMediaSpecs variant="logos">
          <Field label="Logo AGDYSA URL">
            <input className={inputClass} value={form.company?.agdysa_logo || ""} onChange={(e) => patch("company.agdysa_logo", e.target.value)} />
          </Field>
          <Field label="Logo LABSYSO URL">
            <input className={inputClass} value={form.company?.labsyso_logo || ""} onChange={(e) => patch("company.labsyso_logo", e.target.value)} />
          </Field>
        </CmsWithMediaSpecs>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="CTA AGDYSA (etiqueta)">
            <input
              className={inputClass}
              value={form.company?.agdysa_cta_label || ""}
              onChange={(e) => patch("company.agdysa_cta_label", e.target.value)}
            />
          </Field>
          <Field label="CTA AGDYSA (URL)">
            <input
              className={inputClass}
              value={form.company?.agdysa_cta_url || ""}
              placeholder="/servicios?id=…"
              onChange={(e) => patch("company.agdysa_cta_url", e.target.value)}
            />
          </Field>
        </div>
        <Field label="Texto LABSYSO">
          <textarea className={textareaClass} value={form.company?.labsyso || ""} onChange={(e) => patch("company.labsyso", e.target.value)} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="CTA LABSYSO (etiqueta)">
            <input
              className={inputClass}
              value={form.company?.labsyso_cta_label || ""}
              onChange={(e) => patch("company.labsyso_cta_label", e.target.value)}
            />
          </Field>
          <Field label="CTA LABSYSO (URL)">
            <input
              className={inputClass}
              value={form.company?.labsyso_cta_url || ""}
              placeholder="/labsyso"
              onChange={(e) => patch("company.labsyso_cta_url", e.target.value)}
            />
          </Field>
        </div>
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
